// Deliver a training session from the engineer's phone (Session AGG): the session's details, the
// points to cover with the group, then the attendees with a signature each. Submitting records the
// attendance and issues the customer's certificate.
//
// LIVE ONLY, deliberately. Training is not held in the offline cache (unlike jobs), because a
// certificate is issued and numbered server-side at the moment of submission: queueing that offline
// would mean either issuing a reference on a phone (which cannot know the tenant's sequence) or
// telling an engineer a certificate exists when nothing has been issued. So the screen asks for a
// connection instead of pretending.
//
// The checklist ticks are screen state only and are never sent: they are there so nothing is missed
// while delivering, not to become a record. Submission is NOT gated on them (an engineer who has
// covered a point without tapping it must still be able to finish), only on having one named
// attendee.
import { presentableServerMessage } from "../../lib/serverMessage";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { GraduationCap, X } from "lucide-react";
import { api, ApiError } from "../../lib/api";
import type { EngineerTrainingSessionDto } from "../../lib/trainingTypes";
import { useOnline } from "../../lib/offline/useOnline";
import { SignaturePad } from "./SignaturePad";

// One attendee row being filled in on the phone. `existingId` marks a row that came from the office:
// sending it back with its id keeps whatever signature is already stored for that person.
interface AttendeeRow {
  key: string;
  existingId?: string;
  name: string;
  role: string;
  signatureData: string | null;
  hadSignature: boolean;
}

let rowSeq = 0;
function newRow(over: Partial<AttendeeRow> = {}): AttendeeRow {
  rowSeq += 1;
  return { key: `row-${rowSeq}`, name: "", role: "", signatureData: null, hadSignature: false, ...over };
}

export function sessionDateLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Date not set";
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}

export function durationLabel(minutes: number | null): string | null {
  if (minutes == null || minutes <= 0) return null;
  if (minutes < 60) return `${minutes} minutes`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} ${h === 1 ? "hour" : "hours"}` : `${h}h ${m}m`;
}

/** Rows worth sending: a row with no name is an unfinished row, not an attendee. */
export function namedRows(rows: AttendeeRow[]): AttendeeRow[] {
  return rows.filter((r) => r.name.trim().length > 0);
}

export default function TrainingSessionScreen() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const online = useOnline();

  const [session, setSession] = useState<EngineerTrainingSessionDto | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "missing" | "error">("loading");
  const [ticked, setTicked] = useState<Record<string, boolean>>({});
  const [rows, setRows] = useState<AttendeeRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // `signed` records whether any signature was actually captured, so the done screen does not call an
  // unsigned attendance record "signed". A signature is optional at every layer.
  const [issued, setIssued] = useState<{ certificateRef: string; filed: boolean; signed: boolean } | null>(null);

  // Loaded ONCE. `loadedRef` is why: an engineer works in a basement machine room, so `online` flips
  // as they move, and re-running this on every flip would reset the screen and DESTROY the names and
  // signatures they had already collected, with the group already dispersed. The session's own facts
  // never change while they are standing there, so there is nothing to refresh and everything to lose.
  const loadedRef = useRef(false);

  const load = useCallback(async () => {
    setState("loading");
    try {
      const all = await api.training.mine();
      const found = all.find((s) => s.id === id) ?? null;
      if (!found) {
        setState("missing");
        return;
      }
      setSession(found);
      // Start from whatever the office already entered, so submitting cannot wipe it.
      setRows(
        found.attendees.map((a) =>
          newRow({ existingId: a.id, name: a.name, role: a.role ?? "", hadSignature: a.hasSignature }),
        ),
      );
      setState("ready");
    } catch {
      setState("error");
    }
  }, [id]);

  useEffect(() => {
    if (loadedRef.current) return;
    if (!online) return;
    loadedRef.current = true;
    void load();
  }, [load, online]);

  const attendees = namedRows(rows);
  const canSubmit = online && !busy && attendees.length > 0;

  async function submit() {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    const anySigned = attendees.some((r) => r.signatureData != null || r.hadSignature);
    try {
      // Attendance first, then the certificate: the certificate is generated FROM the attendees, so
      // issuing it before they are stored would produce a document with nobody on it.
      const saved = await api.training.setAttendees(
        id,
        attendees.map((r) => ({
          ...(r.existingId ? { id: r.existingId } : {}),
          name: r.name.trim(),
          role: r.role.trim() || null,
          ...(r.signatureData ? { signatureData: r.signatureData } : {}),
        })),
      );
      // ADOPT the ids the write just minted. A replace-write gives every row a new id, so holding the
      // old ones would make a retry send ids this session no longer has and silently drop a signature
      // that is already on record. Positional: the server writes and returns them in submitted order.
      setRows((rs) => {
        const named = rs.filter((r) => r.name.trim().length > 0);
        const byKey = new Map(named.map((r, i) => [r.key, saved.attendees[i]]));
        return rs.map((r) => {
          const match = byKey.get(r.key);
          return match ? { ...r, existingId: match.id, hadSignature: match.hasSignature } : r;
        });
      });
      const result = await api.training.completeFromPwa(id);
      setIssued({ certificateRef: result.certificateRef, filed: result.filed, signed: anySigned });
    } catch (err) {
      // A session already completed answers 409 carrying the reference that was issued (from EITHER
      // call: the attendee write refuses a completed session with the reference too). That is the
      // dropped-response retry case, and the work IS done, so it reports as done. It does NOT claim
      // the copy was filed: this call did not file it and cannot know.
      if (err instanceof ApiError && err.status === 409 && typeof err.body?.certificateRef === "string") {
        setIssued({ certificateRef: err.body.certificateRef, filed: false, signed: anySigned });
        setBusy(false);
        return;
      }
      setError(
        err instanceof ApiError && presentableServerMessage(err.message)
          ? presentableServerMessage(err.message)!
          : // Deliberately not "could not record it": a lost response can hide a completion that
            // succeeded, so this says what is actually known and makes the safe retry explicit.
            "We did not get confirmation that the training was recorded. Check your connection and press the button again: it is safe to try twice, and it will tell you if it is already done.",
      );
      setBusy(false);
    }
  }

  // ---- Done ----------------------------------------------------------------
  if (issued) {
    return (
      <div>
        <h1 className="mb-4 font-heading text-2xl tracking-wide text-ele-strong">Training recorded</h1>
        <div className="rounded-lg border border-ele-green bg-ele-card p-4">
          <p className="font-heading text-lg text-ele-strong">Certificate issued: {issued.certificateRef}</p>
          <p className="mt-2 text-sm" style={{ color: "var(--ele-text-muted)" }}>
            {/* Says only what is true: "signed" only when a signature was actually captured, and no
                claim that the copy was filed when it was not. */}
            {issued.filed
              ? `The ${issued.signed ? "signed " : ""}attendance record has been filed and will appear on the customer portal.`
              : `The certificate has been issued and the ${issued.signed ? "signed " : ""}attendance record will appear on the customer portal. The filed copy could not be stored, so tell the office.`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/engineer")}
          className="mt-4 min-h-11 w-full rounded-ele bg-ele-green px-4 py-3 font-heading tracking-wide text-ele-bg"
        >
          Done
        </button>
      </div>
    );
  }

  // ---- Offline / loading / missing ------------------------------------------
  if (!online) {
    return (
      <div>
        <h1 className="mb-4 font-heading text-2xl tracking-wide text-ele-strong">Training</h1>
        <p role="alert" className="rounded-lg border border-ele-card-border bg-ele-card p-4 text-sm text-ele-strong">
          You need to be online to record training attendance. The certificate is issued by the office system when you
          submit.
        </p>
      </div>
    );
  }
  if (state === "loading") {
    return (
      <p className="text-sm" style={{ color: "var(--ele-text-muted)" }}>
        Loading the training session...
      </p>
    );
  }
  if (state === "missing" || state === "error" || !session) {
    return (
      <div>
        <h1 className="mb-4 font-heading text-2xl tracking-wide text-ele-strong">Training</h1>
        <p role="alert" className="rounded-lg border border-ele-card-border bg-ele-card p-4 text-sm text-ele-strong">
          {state === "missing"
            ? "This training session is no longer on your list. It may have been completed or reassigned."
            : "Could not load the training session. Please try again."}
        </p>
        <Link
          to="/engineer/jobs"
          className="mt-4 inline-block min-h-11 rounded-ele border border-ele-card-border px-4 py-3 font-menu text-xs tracking-wide text-ele-strong"
        >
          Back to my jobs
        </Link>
      </div>
    );
  }

  const checklist = session.trainingChecklist;
  const coveredCount = checklist.filter((c) => ticked[c.id]).length;
  const duration = durationLabel(session.durationMinutes);

  return (
    <div className="pb-4">
      <h1 className="mb-1 flex items-start gap-2 font-heading text-2xl tracking-wide text-ele-strong">
        <GraduationCap size={22} className="mt-1 flex-none" color="#7d21c7" aria-hidden="true" />
        <span>{session.typeLabel}</span>
      </h1>

      {/* Section 1: the session, read only. */}
      <section aria-labelledby="training-details" className="mt-4 rounded-lg border border-ele-card-border bg-ele-card p-3">
        <h2 id="training-details" className="sr-only">
          Session details
        </h2>
        <p className="font-heading text-[15px] font-bold text-ele-strong">{session.siteName || "Site to be confirmed"}</p>
        {session.siteAddress && (
          <p className="mt-0.5 text-[13px]" style={{ color: "var(--ele-text-muted)" }}>
            {session.siteAddress}
            {session.sitePostcode ? `, ${session.sitePostcode}` : ""}
          </p>
        )}
        <p className="mt-2 text-[13px] text-ele-strong">{sessionDateLabel(session.sessionDate)}</p>
        {duration && (
          <p className="text-[13px]" style={{ color: "var(--ele-text-muted)" }}>
            Planned duration: {duration}
          </p>
        )}
        {session.lifts.length > 0 && (
          <p className="mt-2 text-[13px]" style={{ color: "var(--ele-loler-ink)" }}>
            {session.lifts.map((l) => l.eleNumber ?? "Reference pending").join(", ")}
          </p>
        )}
      </section>

      {/* Section 2: the delivery checklist. Screen state only, never submitted. */}
      {checklist.length > 0 && (
        <section aria-labelledby="training-checklist" className="mt-6">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <h2 id="training-checklist" className="font-heading text-base text-ele-strong">
              Cover each point with the group
            </h2>
            <span
              data-testid="checklist-progress"
              className="rounded-full px-2 py-0.5 font-menu text-[10px] uppercase tracking-wide"
              style={{ backgroundColor: "rgba(125,33,199,0.16)", color: "#7d21c7" }}
            >
              {coveredCount} of {checklist.length} points covered
            </span>
          </div>
          <ul className="space-y-2">
            {checklist.map((item) => (
              <li key={item.id}>
                <label className="flex min-h-11 cursor-pointer gap-3 rounded-lg border border-ele-card-border bg-ele-card p-3">
                  <input
                    type="checkbox"
                    checked={Boolean(ticked[item.id])}
                    onChange={(e) => setTicked((t) => ({ ...t, [item.id]: e.target.checked }))}
                    className="mt-0.5 h-5 w-5 flex-none accent-ele-green"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[14px] text-ele-strong">{item.text}</span>
                    {item.warning && (
                      <span className="mt-1 block text-[13px]" style={{ color: "var(--ele-amber-text-deep)" }}>
                        {item.warning}
                      </span>
                    )}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Section 3: who attended, and their signatures. */}
      <section aria-labelledby="training-attendees" className="mt-6">
        <h2 id="training-attendees" className="mb-2 font-heading text-base text-ele-strong">
          Who attended
        </h2>
        {rows.length === 0 && (
          <p className="rounded-lg border border-ele-card-border bg-ele-card p-4 text-center text-[13px]" style={{ color: "var(--ele-text-muted)" }}>
            Add each person who attended, and ask them to sign.
          </p>
        )}
        <ul className="space-y-3">
          {rows.map((row, index) => (
            <li key={row.key} className="rounded-lg border border-ele-card-border bg-ele-card p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="font-menu text-[11px] uppercase tracking-wide" style={{ color: "var(--ele-text-muted)" }}>
                  Attendee {index + 1}
                </span>
                <button
                  type="button"
                  onClick={() => setRows((rs) => rs.filter((r) => r.key !== row.key))}
                  aria-label={`Remove attendee ${index + 1}`}
                  className="flex h-11 w-11 items-center justify-center"
                  style={{ color: "var(--ele-danger)" }}
                >
                  <X size={18} aria-hidden="true" />
                </button>
              </div>
              <label className="mb-1 block font-menu text-[11px] uppercase tracking-wide" style={{ color: "var(--ele-text-muted)" }} htmlFor={`${row.key}-name`}>
                Name
              </label>
              <input
                id={`${row.key}-name`}
                value={row.name}
                onChange={(e) => setRows((rs) => rs.map((r) => (r.key === row.key ? { ...r, name: e.target.value } : r)))}
                className="mb-3 min-h-11 w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-[15px] text-ele-strong"
              />
              <label className="mb-1 block font-menu text-[11px] uppercase tracking-wide" style={{ color: "var(--ele-text-muted)" }} htmlFor={`${row.key}-role`}>
                Role (optional)
              </label>
              <input
                id={`${row.key}-role`}
                value={row.role}
                placeholder="e.g. Building manager"
                onChange={(e) => setRows((rs) => rs.map((r) => (r.key === row.key ? { ...r, role: e.target.value } : r)))}
                className="mb-3 min-h-11 w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-[15px] text-ele-strong"
              />
              <SignaturePad
                placeholder={row.hadSignature && !row.signatureData ? "Already signed. Sign again to replace." : "Sign here"}
                clearLabel="Clear"
                onChange={(dataUrl) => setRows((rs) => rs.map((r) => (r.key === row.key ? { ...r, signatureData: dataUrl } : r)))}
              />
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={() => setRows((rs) => [...rs, newRow()])}
          className="mt-3 min-h-11 w-full rounded-ele border border-ele-green px-4 py-3 font-menu text-xs tracking-wide text-ele-green"
        >
          Add attendee
        </button>
      </section>

      {error && (
        <p role="alert" className="mt-4 rounded-lg border p-3 text-sm text-ele-strong" style={{ borderColor: "var(--ele-danger)" }}>
          {error}
        </p>
      )}

      <button
        type="button"
        onClick={() => void submit()}
        disabled={!canSubmit}
        className="mt-4 min-h-11 w-full rounded-ele bg-ele-green px-4 py-3 font-heading tracking-wide text-ele-bg disabled:opacity-50"
      >
        {busy ? "Recording..." : "Complete and issue certificate"}
      </button>
      {attendees.length === 0 && (
        <p className="mt-2 text-center text-[13px]" style={{ color: "var(--ele-text-muted)" }}>
          Add at least one attendee with a name before finishing.
        </p>
      )}
    </div>
  );
}
